import { useState, useEffect, FormEvent, type ReactNode } from 'react';
import { useBlocker } from 'react-router-dom';
import AppLayout from '../components/Layout/AppLayout';
import AdminLayout from '../components/Layout/AdminLayout';
import ReviewerLayout from '../components/Layout/ReviewerLayout';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { validatePassword, PasswordStrengthBar } from '../utils/passwordUtils';
import PasswordInput from '../components/Auth/PasswordInput';
import type { ProfileResponse } from '@vedjegy/shared';

type Tab = 'company' | 'password';

const SIZE_LABELS: Record<string, string> = {
  mikro:   'Mikrovállalkozás (0–9 fő)',
  kis:     'Kisvállalkozás (10–49 fő)',
  kozepes: 'Középvállalkozás (50–249 fő)',
  nagy:    'Nagyvállalat (250+ fő)',
};

function deriveSizeCategory(count: string): string {
  const n = parseInt(count, 10);
  if (isNaN(n) || count === '') return '';
  if (n <= 9)   return 'mikro';
  if (n <= 49)  return 'kis';
  if (n <= 249) return 'kozepes';
  return 'nagy';
}

// Returns an error string if the zip is invalid, empty string if valid.
// Hungarian zip: exactly 4 digits.
// Foreign zip: 2–10 alphanumeric characters (covers most international formats).
function checkZipFormat(zip: string, country: string): string {
  const v = zip.trim();
  const isHungary = !country.trim() || country.trim().toLowerCase() === 'magyarország';
  if (isHungary) {
    if (!/^\d{4}$/.test(v)) return 'Magyar irányítószám: pontosan 4 számjegy szükséges.';
  } else {
    if (!/^[A-Za-z0-9][A-Za-z0-9\s\-]{0,9}$/.test(v) || v.length < 2)
      return 'Irányítószám: 2–10 alfanumerikus karakter engedélyezett.';
  }
  return '';
}

export default function ProfilePage() {
  const { user } = useAuth();
  const isAdmin    = user?.roles.some((r) => ['tenant_admin', 'super_admin'].includes(r)) ?? false;
  const isReviewer = user?.roles.some((r) => r === 'reviewer') ?? false;

  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState<Tab>('company');

  // Company form state
  const [form, setForm]         = useState<Record<string, string>>({});
  // savedForm tracks the last persisted state to detect unsaved changes
  const [savedForm, setSavedForm] = useState<Record<string, string>>({});
  const [saving, setSaving]     = useState(false);
  const [saveMsg, setSaveMsg]   = useState('');
  const [saveError, setSaveError] = useState('');

  // "Billing address same as HQ" convenience checkbox
  const [billingSameAsHq, setBillingSameAsHq] = useState(false);

  // Password form state
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving]   = useState(false);
  const [pwMsg, setPwMsg]         = useState('');
  const [pwError, setPwError]     = useState('');

  useEffect(() => {
    api.profile.get()
      .then((p) => {
        setProfile(p);
        // Admins and reviewers have no company — show only the password tab
        if (!p.company) setTab('password');
        if (p.company) {
          const initial: Record<string, string> = {
            companyName:   p.company.name ?? '',
            companyType:   p.company.type ?? '',
            employeeCount: p.company.employeeCount != null ? String(p.company.employeeCount) : '',
            sizeCategory:  p.company.sizeCategory ?? '',
            hqZip:         p.company.hqZip ?? '',
            hqCity:        p.company.hqCity ?? '',
            hqStreet:      p.company.hqStreet ?? '',
            mailZip:       p.company.mailZip ?? '',
            mailCity:      p.company.mailCity ?? '',
            mailStreet:    p.company.mailStreet ?? '',
            mailPoBox:     p.company.mailPoBox ?? '',
            websiteUrl:     p.company.websiteUrl ?? '',
            facebookUrl:    p.company.facebookUrl ?? '',
            instagramUrl:   p.company.instagramUrl ?? '',
            youtubeUrl:     p.company.youtubeUrl ?? '',
            linkedinUrl:    p.company.linkedinUrl ?? '',
            billingName:    p.company.billingName ?? '',
            billingZip:     p.company.billingZip ?? '',
            billingCity:    p.company.billingCity ?? '',
            billingStreet:  p.company.billingStreet ?? '',
            billingCountry: p.company.billingCountry ?? 'Magyarország',
          };
          setForm(initial);
          setSavedForm(initial);
        }
      })
      .catch(() => { /* handled below */ })
      .finally(() => setLoading(false));
  }, []);

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedForm);

  // Block React Router in-app navigation when there are unsaved changes
  const blocker = useBlocker(isDirty);

  // Block browser tab close / refresh when there are unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) { e.preventDefault(); }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);


  function f(key: string) { return form[key] ?? ''; }
  function set(key: string) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.value;
      setForm((prev) => {
        const next = { ...prev, [key]: value };
        // If billing-same-as-hq is active and a HQ field changed, sync to billing
        if (billingSameAsHq) {
          if (key === 'hqZip')      next.billingZip    = value;
          if (key === 'hqCity')     next.billingCity   = value;
          if (key === 'hqStreet')   next.billingStreet = value;
          if (key === 'companyName') next.billingName  = value;
        }
        return next;
      });
    };
  }

  function handleBillingSameAsHq(checked: boolean) {
    setBillingSameAsHq(checked);
    if (checked) {
      setForm((prev) => ({
        ...prev,
        billingName:   prev.companyName ?? '',
        billingZip:    prev.hqZip       ?? '',
        billingCity:   prev.hqCity      ?? '',
        billingStreet: prev.hqStreet    ?? '',
        billingCountry: prev.billingCountry || 'Magyarország',
      }));
    }
  }

  async function handleSaveCompany(e: FormEvent) {
    e.preventDefault();
    setSaveMsg(''); setSaveError('');

    // Always-required fields
    const required: Array<{ key: string; label: string }> = [
      { key: 'companyName',    label: 'Vállalkozás neve' },
      { key: 'hqZip',         label: 'Székhely – irányítószám' },
      { key: 'hqCity',        label: 'Székhely – település' },
      { key: 'hqStreet',      label: 'Székhely – utca, házszám' },
      { key: 'billingName',   label: 'Számlázási név' },
      { key: 'billingZip',    label: 'Számlázási cím – irányítószám' },
      { key: 'billingCity',   label: 'Számlázási cím – település' },
      { key: 'billingStreet', label: 'Számlázási cím – utca, házszám' },
      { key: 'billingCountry',label: 'Számlázási cím – ország' },
    ];
    const missing = required.filter(({ key }) => !form[key]?.trim());
    if (missing.length > 0) {
      setSaveError(`Kötelező mezők hiányoznak: ${missing.map((x) => x.label).join(', ')}`);
      return;
    }

    // Mail address: optional but if any field is filled, all must be filled
    const mailFields: Array<{ key: string; label: string }> = [
      { key: 'mailZip',    label: 'Levelezési cím – irányítószám' },
      { key: 'mailCity',   label: 'Levelezési cím – település' },
      { key: 'mailStreet', label: 'Levelezési cím – utca, házszám' },
    ];
    const mailFilled = mailFields.filter(({ key }) => form[key]?.trim());
    const mailMissing = mailFields.filter(({ key }) => !form[key]?.trim());
    if (mailFilled.length > 0 && mailMissing.length > 0) {
      setSaveError(`Ha a levelezési címet kitölti, minden mező kötelező: ${mailMissing.map((x) => x.label).join(', ')}`);
      return;
    }

    // Postal code format validation
    const zipErrors: string[] = [];
    const hqZipErr = checkZipFormat(form.hqZip ?? '', 'Magyarország');
    if (hqZipErr) zipErrors.push(`Székhely irányítószám: ${hqZipErr}`);

    // Mailing zip only validated if mailing address is being filled in
    if (mailFilled.length > 0 && form.mailZip?.trim()) {
      const mailZipErr = checkZipFormat(form.mailZip, 'Magyarország');
      if (mailZipErr) zipErrors.push(`Levelezési irányítószám: ${mailZipErr}`);
    }

    const billingZipErr = checkZipFormat(form.billingZip ?? '', form.billingCountry ?? '');
    if (billingZipErr) zipErrors.push(`Számlázási irányítószám: ${billingZipErr}`);

    if (zipErrors.length > 0) {
      setSaveError(zipErrors.join(' — '));
      return;
    }

    setSaving(true);
    try {
      await api.profile.update({
        companyName:   form.companyName || undefined,
        companyType:   (form.companyType as 'sole_trader' | 'company') || undefined,
        employeeCount: form.employeeCount ? parseInt(form.employeeCount, 10) : null,
        sizeCategory:  (form.sizeCategory as 'mikro'|'kis'|'kozepes'|'nagy') || null,
        hqZip:         form.hqZip || null,
        hqCity:        form.hqCity || null,
        hqStreet:      form.hqStreet || null,
        mailZip:       form.mailZip || null,
        mailCity:      form.mailCity || null,
        mailStreet:    form.mailStreet || null,
        mailPoBox:     form.mailPoBox || null,
        websiteUrl:     form.websiteUrl || null,
        facebookUrl:    form.facebookUrl || null,
        instagramUrl:   form.instagramUrl || null,
        youtubeUrl:     form.youtubeUrl || null,
        linkedinUrl:    form.linkedinUrl || null,
        billingName:    form.billingName || null,
        billingZip:     form.billingZip || null,
        billingCity:    form.billingCity || null,
        billingStreet:  form.billingStreet || null,
        billingCountry: form.billingCountry || null,
      });
      setSaveMsg('Adatok sikeresen mentve.');
      setSavedForm({ ...form }); // mark as clean after successful save
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Mentési hiba.');
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword(e: FormEvent) {
    e.preventDefault();
    setPwMsg(''); setPwError('');
    const pwErr = validatePassword(newPw);
    if (pwErr) { setPwError(pwErr); return; }
    if (newPw !== confirmPw) { setPwError('Az új jelszavak nem egyeznek.'); return; }
    setPwSaving(true);
    try {
      await api.profile.changePassword({ currentPassword: currentPw, newPassword: newPw });
      setPwMsg('Jelszó sikeresen megváltoztatva.');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Hiba a jelszócsere során.');
    } finally {
      setPwSaving(false);
    }
  }

  // Pick the correct shell layout based on the user's role
  const Layout = isAdmin ? AdminLayout : isReviewer ? ReviewerLayout : AppLayout;

  if (loading) return (
    <Layout>
      <div style={{ padding: 40, color: 'var(--muted-foreground)' }}>Betöltés…</div>
    </Layout>
  );

  return (
    <>
    {blocker.state === 'blocked' && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}>
        <div style={{
          background: 'var(--card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '28px 32px',
          maxWidth: 400, width: '100%', boxShadow: 'var(--shadow-lg)',
        }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>
            Nem mentett változtatások
          </h2>
          <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--muted-foreground)' }}>
            A profil adataid nincsenek elmentve. Ha most elhagyod az oldalt, a változtatások elvesznek.
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn btn-destructive" onClick={() => blocker.proceed?.()}>
              Kilépés mentés nélkül
            </button>
            <button className="btn btn-ghost" onClick={() => blocker.reset?.()}>
              Maradok
            </button>
          </div>
        </div>
      </div>
    )}
    <Layout>
      <div style={{ padding: '32px 40px', maxWidth: 2000 }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 6 }}>
            FIÓK
          </div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--primary)' }}>Profil</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--muted-foreground)' }}>
            {profile?.email}
            {profile?.company && (
              <span style={{ marginLeft: 10, padding: '2px 8px', background: 'var(--muted)', borderRadius: 20, fontSize: 11, fontWeight: 700, color: 'var(--primary)' }}>
                {profile.company.taxId}
              </span>
            )}
          </p>
        </div>

        {/* Tabs — company tab only shown when user has a linked company */}
        <div style={{ display: 'flex', borderBottom: '2px solid var(--border)', marginBottom: 28 }}>
          {(profile?.company ? (['company', 'password'] as Tab[]) : (['password'] as Tab[])).map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '10px 20px', fontWeight: 700, fontSize: 14,
              background: 'none', border: 'none', cursor: 'pointer',
              color: tab === t ? 'var(--primary)' : 'var(--muted-foreground)',
              borderBottom: `2px solid ${tab === t ? 'var(--primary)' : 'transparent'}`,
              marginBottom: -2,
            }}>
              {t === 'company' ? 'Cégadatok' : 'Jelszócsere'}
            </button>
          ))}
        </div>

        {/* ── Company tab ── */}
        {tab === 'company' && (
          <form onSubmit={(e) => void handleSaveCompany(e)}>
            {isDirty && !saveMsg && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'var(--warning-bg, #fffbeb)', border: '1px solid var(--warning-border, #f59e0b)',
                borderRadius: 'var(--radius)', padding: '10px 16px', marginBottom: 20,
                fontSize: 13, color: 'var(--warning-fg, #92400e)',
              }}>
                <span>⚠ Nem mentett változtatások vannak.</span>
                <button type="submit" className="btn btn-primary" disabled={saving} style={{ fontSize: 13, padding: '4px 14px' }}>
                  {saving ? 'Mentés…' : 'Mentés →'}
                </button>
              </div>
            )}
            {saveMsg   && <div className="alert alert-success"    style={{ marginBottom: 20 }}><span>✓</span><div><p>{saveMsg}</p></div></div>}
            {saveError && <div className="alert alert-destructive" style={{ marginBottom: 20 }}><span>⚠</span><div><p>{saveError}</p></div></div>}

            <Section title="Alapadatok">
              <Row>
                <Field label="Vállalkozás neve" required>
                  <input type="text" value={f('companyName')} onChange={set('companyName')} required />
                </Field>
                <Field label="Vállalkozás típusa" style={{ maxWidth: 220 }}>
                  <select value={f('companyType')} onChange={set('companyType')}>
                    <option value="">— válasszon —</option>
                    <option value="sole_trader">Egyéni vállalkozó</option>
                    <option value="company">Gazdasági társaság</option>
                  </select>
                </Field>
              </Row>
              <Row>
                <Field label="Alkalmazottak száma">
                  <input
                    type="number" min={0} value={f('employeeCount')}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        employeeCount: val,
                        sizeCategory: deriveSizeCategory(val),
                      }));
                    }}
                  />
                </Field>
                <Field label="Méretkategória">
                  <input
                    type="text"
                    readOnly
                    value={f('sizeCategory') ? (SIZE_LABELS[f('sizeCategory')] ?? '') : '— adja meg az alkalmazottak számát —'}
                    style={{ background: 'var(--muted)', cursor: 'default', color: f('sizeCategory') ? 'var(--primary)' : 'var(--muted-foreground)' }}
                  />
                </Field>
              </Row>
            </Section>

            <Section title="Székhely">
              <Row>
                <Field label="Irányítószám" required style={{ maxWidth: 140 }}>
                  <input type="text" value={f('hqZip')} onChange={set('hqZip')} />
                </Field>
                <Field label="Város" required>
                  <input type="text" value={f('hqCity')} onChange={set('hqCity')} />
                </Field>
              </Row>
              <Field label="Utca, házszám" required>
                <input type="text" value={f('hqStreet')} onChange={set('hqStreet')} />
              </Field>
            </Section>

            <Section title="Levelezési cím">
              <Row>
                <Field label="Irányítószám" style={{ maxWidth: 140 }}>
                  <input type="text" value={f('mailZip')} onChange={set('mailZip')} />
                </Field>
                <Field label="Város">
                  <input type="text" value={f('mailCity')} onChange={set('mailCity')} />
                </Field>
              </Row>
              <Row>
                <Field label="Utca, házszám">
                  <input type="text" value={f('mailStreet')} onChange={set('mailStreet')} />
                </Field>
                <Field label="Postafiók">
                  <input type="text" value={f('mailPoBox')} onChange={set('mailPoBox')} />
                </Field>
              </Row>
            </Section>

            <Section title="Számlázási cím">
              <Field label="Számlázási név" required>
                <input type="text" value={f('billingName')} onChange={set('billingName')}
                  readOnly={billingSameAsHq}
                  placeholder={billingSameAsHq ? undefined : (f('companyName') || 'pl. Példa Kft.')}
                  style={billingSameAsHq ? { background: 'var(--muted)', cursor: 'default' } : undefined} />
              </Field>
              {/* Convenience checkbox: copy HQ address into billing fields */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={billingSameAsHq}
                  onChange={(e) => handleBillingSameAsHq(e.target.checked)}
                  style={{ width: 15, height: 15, cursor: 'pointer' }}
                />
                A számlázási cím megegyezik a székhellyel
              </label>
              <Row>
                <Field label="Irányítószám" required style={{ maxWidth: 140 }}>
                  <input type="text" value={f('billingZip')} onChange={set('billingZip')}
                    readOnly={billingSameAsHq}
                    style={billingSameAsHq ? { background: 'var(--muted)', cursor: 'default' } : undefined} />
                </Field>
                <Field label="Helység" required>
                  <input type="text" value={f('billingCity')} onChange={set('billingCity')}
                    readOnly={billingSameAsHq}
                    style={billingSameAsHq ? { background: 'var(--muted)', cursor: 'default' } : undefined} />
                </Field>
              </Row>
              <Field label="Utca, házszám" required>
                <input type="text" value={f('billingStreet')} onChange={set('billingStreet')}
                  readOnly={billingSameAsHq}
                  style={billingSameAsHq ? { background: 'var(--muted)', cursor: 'default' } : undefined} />
              </Field>
              <Field label="Ország" required>
                <input type="text" value={f('billingCountry')} onChange={set('billingCountry')} />
              </Field>
            </Section>

            <div style={{ paddingTop: 8 }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'Mentés…' : 'Mentés →'}
              </button>
            </div>
          </form>
        )}

        {/* ── Password tab ── */}
        {tab === 'password' && (
          <form onSubmit={(e) => void handleChangePassword(e)} style={{ maxWidth: 420 }}>
            {pwMsg   && <div className="alert alert-success"    style={{ marginBottom: 20 }}><span>✓</span><div><p>{pwMsg}</p></div></div>}
            {pwError && <div className="alert alert-destructive" style={{ marginBottom: 20 }}><span>⚠</span><div><p>{pwError}</p></div></div>}

            <div className="field">
              <label>Jelenlegi jelszó <span className="req">*</span></label>
              <PasswordInput value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required autoComplete="current-password" />
            </div>
            <div className="field">
              <label>Új jelszó <span className="req">*</span></label>
              <PasswordInput value={newPw} onChange={(e) => setNewPw(e.target.value)} required autoComplete="new-password"
                placeholder="Min. 8 kar., kis/nagybetű, szám, spec." />
              <PasswordStrengthBar password={newPw} />
            </div>
            <div className="field" style={{ marginBottom: 24 }}>
              <label>Új jelszó megerősítése <span className="req">*</span></label>
              <PasswordInput value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required autoComplete="new-password" />
            </div>

            <button className="btn btn-primary" type="submit" disabled={pwSaving}>
              {pwSaving ? 'Mentés…' : 'Jelszó módosítása →'}
            </button>
          </form>
        )}
      </div>
    </Layout>
    </>
  );
}

// ── Small layout helpers ────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </div>
  );
}

function Row({ children }: { children: ReactNode }) {
  return <div style={{ display: 'flex', gap: 16 }}>{children}</div>;
}

function Field({ label, required, children, style }: { label: string; required?: boolean; children: ReactNode; style?: React.CSSProperties }) {
  return (
    <div className="field" style={{ flex: 1, marginBottom: 0, ...style }}>
      <label>{label}{required && <span className="req"> *</span>}</label>
      {children}
    </div>
  );
}
